// @ts-nocheck

import type { ObjectValues } from '@/types';
import { toScreamingSnakeCase } from '../utils';
import type { ScreamingSnakeCase } from 'type-fest';

export * from './adyen-business-structures';
export * from './adyen-business-types';
export * from './check-error-messages';
export * from './stripe-business-structures';
export * from './stripe-business-types';

export const StateTag = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  RESOLVED: 'resolved',
  REVISION: 'revision',
  EDIT: 'edit',
  MANUAL_REVIEW: 'manual_review',
  PENDING_PROCESS: 'pending_process',
  COLLECTION_FLOW: 'collection_flow',
  FAILURE: 'failure',
  DATA_ENRICHMENT: 'data_enrichment',
  FLAGGED: 'flagged',
  DISMISSED: 'dismissed',
} as const;

export const StateTags = [
  StateTag.APPROVED,
  StateTag.REJECTED,
  StateTag.RESOLVED,
  StateTag.REVISION,
  StateTag.EDIT,
  StateTag.MANUAL_REVIEW,
  StateTag.PENDING_PROCESS,
  StateTag.COLLECTION_FLOW,
  StateTag.FAILURE,
  StateTag.DATA_ENRICHMENT,
  StateTag.FLAGGED,
  StateTag.DISMISSED,
] as const satisfies ReadonlyArray<(typeof StateTag)[keyof typeof StateTag]>;

export const CommonWorkflowEvent = {
  START: 'START',
  CASE_REVIEWED: 'CASE_REVIEWED',
  RETURN_TO_REVIEW: 'RETURN_TO_REVIEW',
  RESUBMITTED: 'RESUBMITTED',
  REJECT: 'reject',
  APPROVE: 'approve',
  REVISION: 'revision',
  RESOLVE: 'resolve',
  FLAG: 'flag',
  DISMISS: 'dismiss',
  EDIT: 'edit',
} as const;

export const CommonWorkflowStates = {
  IDLE: 'idle',
  MANUAL_REVIEW: 'manual_review',
  REJECTED: 'rejected',
  APPROVED: 'approved',
  RESOLVED: 'resolved',
  REVISION: 'revision',
  FLAGGED: 'flagged',
  DISMISSED: 'dismissed',
} as const;

export type TStateTag = (typeof StateTags)[number];

export const WorkflowDefinitionVariant = {
  WEBSITE_MONITORING: 'WEBSITE_MONITORING',
  MANUAL_REVIEW: 'MANUAL_REVIEW',
  KYB: 'KYB',
  KYB_WITH_ASSOCIATED_COMPANIES: 'KYB_WITH_ASSOCIATED_COMPANIES',
  KYC: 'KYC',
  DEFAULT: 'DEFAULT',
  ONGOING: 'ONGOING',
  AML: 'AML',
} as const;

export type TStateTags = typeof StateTags;

export const ProcessStatus = {
  DEFAULT: 'DEFAULT',
  IDLE: 'IDLE',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  CANCELED: 'CANCELED',
} as const;

export const ProcessStatuses = [
  ProcessStatus.IDLE,
  ProcessStatus.IN_PROGRESS,
  ProcessStatus.SUCCESS,
  ProcessStatus.ERROR,
  ProcessStatus.CANCELED,
] as const satisfies ReadonlyArray<(typeof ProcessStatus)[keyof typeof ProcessStatus]>;

export type TProcessStatus = (typeof ProcessStatuses)[number];

export type TProcessStatuses = typeof ProcessStatuses;

export const UnifiedApiReason = {
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
} as const;

export const UnifiedApiReasons = [
  UnifiedApiReason.NOT_IMPLEMENTED,
  UnifiedApiReason.NOT_AVAILABLE,
] as const satisfies ReadonlyArray<(typeof UnifiedApiReason)[keyof typeof UnifiedApiReason]>;

export type TUnifiedApiReason = (typeof UnifiedApiReasons)[number];

export type TUnifiedApiReasons = typeof UnifiedApiReasons;

export const WorkflowDefinitionConfigThemeEnum = {
  KYC: 'kyc',
  KYB: 'kyb',
  DOCUMENTS_REVIEW: 'documents-review',
} as const;

export const WorkflowDefinitionConfigThemes = [
  WorkflowDefinitionConfigThemeEnum.KYB,
  WorkflowDefinitionConfigThemeEnum.KYC,
  WorkflowDefinitionConfigThemeEnum.DOCUMENTS_REVIEW,
] as const satisfies ReadonlyArray<
  (typeof WorkflowDefinitionConfigThemeEnum)[keyof typeof WorkflowDefinitionConfigThemeEnum]
>;

export type TWorkflowDefinitionConfigTheme = (typeof WorkflowDefinitionConfigThemes)[number];

export const Severity = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const;

export const Severities = ['critical', 'high', 'medium', 'low'] as const;

export type SeverityType = (typeof Severities)[number];

export type SeveritiesType = typeof Severities;

export const MatchResponseCode = {
  M00: 'M00',
  M01: 'M01',
  M02: 'M02',
} as const;

export const MatchResponseCodes = [
  MatchResponseCode.M00,
  MatchResponseCode.M01,
  MatchResponseCode.M02,
] as const satisfies ReadonlyArray<ObjectValues<typeof MatchResponseCode>>;

export const MatchReasonCode = {
  '00': 'Questionable Merchant/Under Investigation',
  '01': 'Account Data Compromise',
  '02': 'Common Point of Purchase (CPP)',
  '03': 'Laundering',
  '04': 'Excessive Chargebacks',
  '05': 'Excessive Fraud',
  '06': 'Reserved for Future Use',
  '08': 'Mastercard Questionable Merchant Audit Program',
  '09': 'Bankruptcy/Liquidation/Insolvency',
  '10': 'Violation of Standards',
  '11': 'Merchant Collusion',
  '12': 'PCI Data Security Standard Noncompliance',
  '13': 'Illegal Transactions',
  '14': 'Identity Theft',
  '20': 'Mastercard Questionable Merchant Audit Program',
  '21': 'Listing under Privacy Review',
  '24': 'Illegal Transactions',
} as const;

export const URL_PATTERN =
  /^(?:https?:\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00A1-\uFFFF][a-z0-9\u00A1-\uFFFF_-]{0,62})?[a-z0-9\u00A1-\uFFFF]\.)+(?:[a-z\u00A1-\uFFFF]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

export const CHECKS_STATUSES = ['in-progress', 'failed', 'completed'] as const;

export type ChecksStatus = (typeof CHECKS_STATUSES)[number];

export const CHECKS_STATUSES_MAP = Object.fromEntries(
  CHECKS_STATUSES.map(status => [status, status]),
) as { [K in ChecksStatus]: K };

export const ASSESSMENT_TYPES = [
  'kyb_and_ownership',
  'individual_verification',
  'company_registry_information',
  'company_sanctions',
  'company_structure',
  'individual_sanctions',
] as const;

export type AssessmentType = (typeof ASSESSMENT_TYPES)[number];

export const ASSESSMENT_TYPES_MAP = Object.fromEntries(
  ASSESSMENT_TYPES.map(type => [type, type]),
) as { [K in AssessmentType]: K };

export const ASSESSMENT_STATUSES = [
  'in-progress',
  'pending-review',
  'under-review',
  'approved',
  'rejected',
  'failed',
] as const;

export type AssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];

export const ASSESSMENT_STATUSES_MAP = Object.fromEntries(
  ASSESSMENT_STATUSES.map(status => [toScreamingSnakeCase(status), status]),
) as { [K in AssessmentStatus as ScreamingSnakeCase<K>]: K };

export const UPDATEABLE_ASSESSMENT_STATUSES = [
  ASSESSMENT_STATUSES_MAP.PENDING_REVIEW,
  ASSESSMENT_STATUSES_MAP.UNDER_REVIEW,
  ASSESSMENT_STATUSES_MAP.APPROVED,
  ASSESSMENT_STATUSES_MAP.REJECTED,
] as const;

export type UpdateableAssessmentStatus = (typeof UPDATEABLE_ASSESSMENT_STATUSES)[number];

export const INDIVIDUAL_SANCTION_TYPES = [
  'pep',
  'warnings',
  'sanctions',
  'adverseMedia',
  'fitnessProbity',
] as const;

export type IndividualSanctionType = (typeof INDIVIDUAL_SANCTION_TYPES)[number];

export const INDIVIDUAL_SANCTION_TYPES_MAP = Object.fromEntries(
  INDIVIDUAL_SANCTION_TYPES.map(type => [type, type]),
) as { [K in IndividualSanctionType]: K };

export const INDIVIDUAL_SANCTION_TYPE_LABELS = {
  pep: 'PEP',
  warnings: 'Warnings',
  sanctions: 'Sanctions',
  adverseMedia: 'Adverse Media',
  fitnessProbity: 'Fitness & Probity',
} as const satisfies Record<IndividualSanctionType, string>;

export const REPORT_WORKFLOW_STATES = [
  'collecting-data',
  'running-checks',
  'analyzing-results',
  'finalizing-report',
  'failed',
  'done',
] as const;

export type ReportWorkflowState = (typeof REPORT_WORKFLOW_STATES)[number];

export const REPORT_WORKFLOW_STATES_MAP = Object.fromEntries(
  REPORT_WORKFLOW_STATES.map(state => [state, state]),
) as { [K in ReportWorkflowState]: K };

/** @deprecated Use REPORT_WORKFLOW_STATES instead */
export const DIGITAL_FOOTPRINT_REPORT_STATES = REPORT_WORKFLOW_STATES;

/** @deprecated Use ReportWorkflowState instead */
export type DigitalFootprintReportState = ReportWorkflowState;

/** @deprecated Use REPORT_WORKFLOW_STATES_MAP instead */
export const DIGITAL_FOOTPRINT_REPORT_STATES_MAP = REPORT_WORKFLOW_STATES_MAP;

export const MONITORING_SCHEDULE_TYPES = [
  'weekly',
  'monthly',
  'twice_monthly',
  'every_two_months',
  'custom',
] as const;

export type MonitoringScheduleType = (typeof MONITORING_SCHEDULE_TYPES)[number];

export const MONITORING_SCHEDULE_FILTER_TYPES = [...MONITORING_SCHEDULE_TYPES, 'inactive'] as const;

export type MonitoringScheduleFilterType = (typeof MONITORING_SCHEDULE_FILTER_TYPES)[number];

export const MONITORING_SCHEDULE_DATA_TYPES = [...MONITORING_SCHEDULE_TYPES, 'one-time'] as const;

export type MonitoringScheduleDataType = (typeof MONITORING_SCHEDULE_DATA_TYPES)[number];

export const MERCHANT_REPORT_STATUSES = [
  'in-progress',
  'quality-control',
  'pending-review',
  'under-review',
  'failed',
  'completed',
  'cleared',
  'conditionally-approved',
  'terminated',
] as const;

export type MerchantReportStatus = (typeof MERCHANT_REPORT_STATUSES)[number];

export const MERCHANT_REPORT_STATUSES_MAP = Object.fromEntries(
  MERCHANT_REPORT_STATUSES.map(status => [status, status]),
) as { [K in MerchantReportStatus]: K };

export type UpdateableReportStatus =
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['cleared']
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['conditionally-approved']
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['terminated']
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['completed']
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['pending-review']
  | (typeof MERCHANT_REPORT_STATUSES_MAP)['under-review'];

export const UPDATEABLE_REPORT_STATUSES = [
  MERCHANT_REPORT_STATUSES_MAP['pending-review'],
  MERCHANT_REPORT_STATUSES_MAP['under-review'],
  MERCHANT_REPORT_STATUSES_MAP.completed,
  MERCHANT_REPORT_STATUSES_MAP.cleared,
  MERCHANT_REPORT_STATUSES_MAP['conditionally-approved'],
  MERCHANT_REPORT_STATUSES_MAP.terminated,
] as const;

export const MERCHANT_REPORT_STATUS_LABELS: Record<MerchantReportStatus, string> = {
  'in-progress': 'In Progress',
  'quality-control': 'Quality Control',
  'pending-review': 'Pending Review',
  'under-review': 'In Review',
  completed: 'Completed',
  cleared: 'Cleared',
  'conditionally-approved': 'Conditionally Approved',
  terminated: 'Terminated',
  failed: 'Failed',
};

export const MERCHANT_REPORT_RISK_TYPES = [
  'chargebacks',
  'contentFindings',
  'reputational',
  'transactionLaundering',
  'legal',
] as const;

export const POINT_OF_SALE_RISK_TYPES = ['chargebacks', 'reputational', 'legal'] as const;

export type PointOfSaleRiskType = (typeof POINT_OF_SALE_RISK_TYPES)[number];

export const POINT_OF_SALE_STATUSES = [
  'in-progress',
  'completed',
  'failed',
  'pending-review',
  'under-review',
  'cleared',
  'conditionally-approved',
  'terminated',
] as const;

export type PointOfSaleStatus = (typeof POINT_OF_SALE_STATUSES)[number];

export const POINT_OF_SALE_STATUSES_MAP = Object.fromEntries(
  POINT_OF_SALE_STATUSES.map(status => [status, status]),
) as { [K in PointOfSaleStatus]: K };

export const MERCHANT_REPORT_TYPES = [
  'MERCHANT_REPORT_T1',
  'MERCHANT_REPORT_T1_LITE',
  'ONGOING_MERCHANT_REPORT_T1',
  'DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
  'DIGITAL_FOOTPRINT_LITE_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_LITE_REPORT',
  'POINT_OF_SALE_REPORT',
  'ONGOING_POINT_OF_SALE_REPORT',
  'COMPANY_ANALYSIS_REPORT',
  'LENDING_RISK_ANALYSIS_REPORT',
  'INSURANCE_RISK_ANALYSIS_REPORT',
  'ISSUING_RISK_ANALYSIS_REPORT',
  'SUPPLIER_ASSESSMENT_REPORT',
  'TRANSACTION_ANALYSIS_REPORT',
] as const;

export type MerchantReportType = (typeof MERCHANT_REPORT_TYPES)[number];

export const MERCHANT_REPORT_TYPES_MAP = Object.fromEntries(
  MERCHANT_REPORT_TYPES.map(type => [type, type]),
) as { [K in MerchantReportType]: K };

export const DIGITAL_FOOTPRINT_LITE_REPORT_TYPES = [
  'DIGITAL_FOOTPRINT_LITE_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_LITE_REPORT',
] as const satisfies MerchantReportType[];

export const DIGITAL_FOOTPRINT_FULL_REPORT_TYPES = [
  'DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
] as const satisfies MerchantReportType[];

export const DIGITAL_FOOTPRINT_REPORT_TYPES = [
  ...DIGITAL_FOOTPRINT_LITE_REPORT_TYPES,
  ...DIGITAL_FOOTPRINT_FULL_REPORT_TYPES,
] as const satisfies MerchantReportType[];

export const DIGITAL_FOOTPRINT_MONITORING_REPORT_TYPES = [
  MERCHANT_REPORT_TYPES_MAP.ONGOING_DIGITAL_FOOTPRINT_REPORT,
  MERCHANT_REPORT_TYPES_MAP.ONGOING_DIGITAL_FOOTPRINT_LITE_REPORT,
] as const satisfies MerchantReportType[];

export type DigitalFootprintReportType = (typeof DIGITAL_FOOTPRINT_REPORT_TYPES)[number];

export const MONITORING_REPORT_TYPES = [
  'ONGOING_MERCHANT_REPORT_T1',
  'ONGOING_DIGITAL_FOOTPRINT_REPORT',
  'ONGOING_DIGITAL_FOOTPRINT_LITE_REPORT',
  'ONGOING_POINT_OF_SALE_REPORT',
] as const satisfies MerchantReportType[];

export const BUSINESS_ANALYSIS_REPORT_TYPES = [
  'POINT_OF_SALE_REPORT',
  'ONGOING_POINT_OF_SALE_REPORT',
  'COMPANY_ANALYSIS_REPORT',
  'LENDING_RISK_ANALYSIS_REPORT',
  'INSURANCE_RISK_ANALYSIS_REPORT',
  'ISSUING_RISK_ANALYSIS_REPORT',
  'SUPPLIER_ASSESSMENT_REPORT',
] as const satisfies MerchantReportType[];

export const BUSINESS_ANALYSIS_REPORT_TYPES_MAP = Object.fromEntries(
  BUSINESS_ANALYSIS_REPORT_TYPES.map(type => [type, type]),
) as { [K in BusinessAnalysisReportType]: K };

export type BusinessAnalysisReportType = (typeof BUSINESS_ANALYSIS_REPORT_TYPES)[number];

export const ONBOARDING_REPORT_TYPES = [
  'MERCHANT_REPORT_T1',
  'DIGITAL_FOOTPRINT_REPORT',
  'DIGITAL_FOOTPRINT_LITE_REPORT',
] as const satisfies MerchantReportType[];

export type MonitoringReportType = (typeof MONITORING_REPORT_TYPES)[number];
export type OnboardingReportType = (typeof ONBOARDING_REPORT_TYPES)[number];

/**
 * Maps a report type to the backoffice URL path segment (the piece between
 * `/{locale}/` and `/{reportId}`). Keep this in sync with the routes defined
 * in `apps/backoffice-v2/src/router.tsx`.
 */
export const MERCHANT_REPORT_TYPE_TO_URL_SEGMENT = {
  MERCHANT_REPORT_T1: 'merchant-monitoring',
  MERCHANT_REPORT_T1_LITE: 'merchant-monitoring',
  ONGOING_MERCHANT_REPORT_T1: 'merchant-monitoring',
  DIGITAL_FOOTPRINT_REPORT: 'digital-footprint',
  ONGOING_DIGITAL_FOOTPRINT_REPORT: 'digital-footprint',
  DIGITAL_FOOTPRINT_LITE_REPORT: 'digital-footprint',
  ONGOING_DIGITAL_FOOTPRINT_LITE_REPORT: 'digital-footprint',
  POINT_OF_SALE_REPORT: 'point-of-sale',
  ONGOING_POINT_OF_SALE_REPORT: 'point-of-sale',
  COMPANY_ANALYSIS_REPORT: 'company-analysis',
  LENDING_RISK_ANALYSIS_REPORT: 'lending-risk-analysis',
  INSURANCE_RISK_ANALYSIS_REPORT: 'insurance-risk-analysis',
  ISSUING_RISK_ANALYSIS_REPORT: 'issuing-risk-analysis',
  SUPPLIER_ASSESSMENT_REPORT: 'supplier-assessment',
  TRANSACTION_ANALYSIS_REPORT: 'transaction-analysis',
} as const satisfies Record<MerchantReportType, string>;

export const MERCHANT_REPORT_URL_SEGMENTS = [
  ...new Set(Object.values(MERCHANT_REPORT_TYPE_TO_URL_SEGMENT)),
] as const;

/**
 * Returns the backoffice URL path segment for a given report type, or
 * `undefined` when the type is unknown so callers can decide on a fallback
 * (e.g. navigate home instead of to a guessed list page).
 */
export const getReportTypeUrlSegment = (
  reportType: string | null | undefined,
): string | undefined => {
  if (!reportType) return undefined;

  return (MERCHANT_REPORT_TYPE_TO_URL_SEGMENT as Record<string, string>)[reportType];
};

export const MERCHANT_REPORT_VERSIONS = ['1', '2', '3'] as const;

export type MerchantReportVersion = (typeof MERCHANT_REPORT_VERSIONS)[number];

export const MERCHANT_REPORT_VERSIONS_MAP = Object.fromEntries(
  MERCHANT_REPORT_VERSIONS.map(version => [version, version]),
) as { [K in MerchantReportVersion]: K };

export const MERCHANT_REPORT_RISK_LEVELS = Severities;

export type MerchantReportRiskLevel = (typeof MERCHANT_REPORT_RISK_LEVELS)[number];

export const MERCHANT_REPORT_RISK_LEVELS_MAP = Object.fromEntries(
  MERCHANT_REPORT_RISK_LEVELS.map(level => [level, level]),
) as { [K in MerchantReportRiskLevel]: K };

export const REPORT_MANUAL_CHANGE_ATTRIBUTE = {
  RISK_LEVEL: 'riskLevel',
} as const;

export const REPORT_MANUAL_CHANGE_ATTRIBUTE_VALUES = Object.values(REPORT_MANUAL_CHANGE_ATTRIBUTE);

export type ReportManualChangeAttribute =
  (typeof REPORT_MANUAL_CHANGE_ATTRIBUTE)[keyof typeof REPORT_MANUAL_CHANGE_ATTRIBUTE];

export const RISK_INDICATOR_RISK_LEVELS = ['positive', 'moderate', 'critical'] as const;

export type RiskIndicatorRiskLevel = (typeof RISK_INDICATOR_RISK_LEVELS)[number];

export const RISK_INDICATOR_RISK_LEVELS_MAP = Object.fromEntries(
  RISK_INDICATOR_RISK_LEVELS.map(level => [level, level]),
) as { [K in RiskIndicatorRiskLevel]: K };

export const SUPPORTED_FILE_EXT_REGEX = /\.(jpg|jpeg|svg|png|pdf|gif|txt|csv|xlsx|xls|mp4|webm)$/i;

// Extract file extensions from regex
export const SUPPORTED_FILE_EXT_ENUM = {
  JPG: 'jpg',
  JPEG: 'jpeg',
  SVG: 'svg',
  PNG: 'png',
  PDF: 'pdf',
  GIF: 'gif',
  TXT: 'txt',
  CSV: 'csv',
  XLSX: 'xlsx',
  XLS: 'xls',
  MP4: 'mp4',
  WEBM: 'webm',
} as const;

export const FILE_MAX_SIZE_IN_KB = 1024;
export const FILE_MAX_SIZE_IN_BYTE = 200 * FILE_MAX_SIZE_IN_KB * 1024; // 200 MB

// validate file exts in enum against regex
Object.entries(SUPPORTED_FILE_EXT_ENUM).forEach(([key, value]) => {
  if (!SUPPORTED_FILE_EXT_REGEX.test(`.${value}`)) {
    throw new Error(`Invalid file extension: .${value}`);
  }
});

export type SupportedFileExt =
  (typeof SUPPORTED_FILE_EXT_ENUM)[keyof typeof SUPPORTED_FILE_EXT_ENUM];

export const BANK_ACCOUNT_VERIFICATION_REQUEST_TYPE = {
  COMMERCIAL: 'BAVCOPCommercialCond',
  CONSUMER: 'BAVCOPConsumerCond',
} as const;

export const BUSINESS_TYPES = [
  'Limited',
  'Public Limited',
  'Limited Liability Partnership',
  'Government/Local Authority',
  'Sole Trader',
  'Partnership',
  'Other',
] as const;

export const NOT_DETECTED = 'Not Detected' as const;

export const TASKS_STATUSES = ['created', 'in-progress', 'completed', 'failed'] as const;

export type TasksStatus = (typeof TASKS_STATUSES)[number];

export const TASKS_STATUSES_MAP = Object.fromEntries(
  TASKS_STATUSES.map(status => [status, status]),
) as { [K in TasksStatus]: K };

export const TASK_TYPES = ['single-prompt-workflow'] as const;

export type TaskType = (typeof TASK_TYPES)[number];

export const TASK_TYPES_MAP = Object.fromEntries(TASK_TYPES.map(type => [type, type])) as {
  [K in TaskType]: K;
};
export const FAKE_URL_FOR_DATASET_BUILDER = 'https://fake-url-for-dataset-builder.com/' as const;

export const SUPPORTED_LANGUAGES = [
  { label: 'English', value: 'original', locale: 'en' },
  { label: 'Chinese', value: 'zh-CN', locale: 'zh-CN' },
  { label: 'Spanish', value: 'es', locale: 'es' },
  { label: 'Portuguese', value: 'pt', locale: 'pt' },
  { label: 'French', value: 'fr', locale: 'fr' },
  { label: 'German', value: 'de', locale: 'de' },
  { label: 'Italian', value: 'it', locale: 'it' },
  { label: 'Thai', value: 'th', locale: 'th' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export type SupportedLanguageValue = SupportedLanguage['value'];

export type SupportedLocale = SupportedLanguage['locale'];

export const SUPPORTED_LOCALES = SUPPORTED_LANGUAGES.map(lang => lang.locale);

export const NON_ENGLISH_LOCALES = SUPPORTED_LOCALES.filter(locale => locale !== 'en');

export const BATCH_WORKFLOW_TYPES = [
  'web-presence',
  'scam-and-fraud',
  'dataset-builder',
  'digital-footprint',
  'prompt-execution',
] as const;

export type BatchWorkflowType = (typeof BATCH_WORKFLOW_TYPES)[number];

export const BATCH_WORKFLOW_TYPES_MAP = Object.fromEntries(
  BATCH_WORKFLOW_TYPES.map(type => [type, type]),
) as {
  [K in BatchWorkflowType]: K;
};

// Define constants locally to avoid CJS/ESM compatibility issues
// These match the values in @ballerine/digital-footprint-schemas
// but are defined here so common's CJS build works without requiring ESM-only packages
export const STAKEHOLDER_ROLES = [
  'director',
  'ubo',
  'senior_officer',
  'signatory',
  'legal_representative',
  'other',
] as const;

export type StakeholderRole = (typeof STAKEHOLDER_ROLES)[number];

export const STAKEHOLDER_ROLES_MAP = {
  director: 'Director',
  ubo: 'UBO',
  senior_officer: 'Senior Officer',
  signatory: 'Signatory',
  legal_representative: 'Legal Representative',
  other: 'Other',
} as const satisfies Record<StakeholderRole, string>;

export const STAKEHOLDER_ROLES_REVERSE_MAP = Object.fromEntries(
  Object.entries(STAKEHOLDER_ROLES_MAP).map(([key, value]) => [value.toLowerCase(), key]),
) as Record<string, StakeholderRole>;

export { COUNTRIES_WITH_STATES as COUNTRIES_REQUIRING_STATE } from '../countries';

export const AUDIT_LOG_ENTITY_TYPES = ['report', 'website', 'note'] as const;

export type AuditLogEntityType = (typeof AUDIT_LOG_ENTITY_TYPES)[number];

export const AUDIT_LOG_ENTITY_TYPES_MAP = Object.fromEntries(
  AUDIT_LOG_ENTITY_TYPES.map(type => [type, type]),
) as { [K in AuditLogEntityType]: K };
